// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.

import { Check, AlertCircle, Clock, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type StatusKind = 'done' | 'attention' | 'progress' | 'empty'

const MAP: Record<StatusKind, { label: string; cls: string; Icon: typeof Check }> = {
  done:      { label: 'Geregeld',         cls: 'bg-status-successBg text-status-successText border-status-successBorder', Icon: Check },
  attention: { label: 'Aandacht nodig',   cls: 'bg-status-warnBg text-status-warnText border-status-warnBorder',        Icon: AlertCircle },
  progress:  { label: 'In behandeling',   cls: 'bg-status-infoBg text-status-infoText border-status-infoBorder',        Icon: Clock },
  empty:     { label: 'Nog niet ingevuld',cls: 'bg-sand-100 text-ink-4 border-sand-300',                                Icon: Circle },
}

/**
 * Eén uniforme statusweergave voor de hele applicatie. Gebruik overal dezelfde
 * stijl: ✓ Geregeld / 🟠 Aandacht nodig / 🔵 In behandeling / ⚪ Nog niet.
 */
export function StatusPill({ status, label, size = 'md', className }: {
  status:     StatusKind
  label?:     string
  size?:      'sm' | 'md'
  className?: string
}) {
  const s = MAP[status]
  const Icon = s.Icon
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap',
      size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
      s.cls,
      className,
    )}>
      <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
      {label ?? s.label}
    </span>
  )
}

/** Vertaalt de modulestatus naar een uniforme StatusPill-soort. */
export function moduleStatusKind(s: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE'): StatusKind {
  return s === 'COMPLETE' ? 'done' : s === 'IN_PROGRESS' ? 'progress' : 'empty'
}
